/**
 * KeyboardNav - Stories 24.84-24.85
 * Keyboard navigation and shortcuts
 */

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Keyboard, Command, ArrowUp, ArrowDown, ChevronRight, X } from 'lucide-react';
import { createPortal } from 'react-dom';

// Keyboard shortcut types
export interface KeyboardShortcut {
  id: string;
  keys: string[];
  description: string;
  category: string;
  action: () => void;
  enabled?: boolean;
}

// Keyboard navigation context
interface KeyboardNavContextType {
  shortcuts: KeyboardShortcut[];
  registerShortcut: (shortcut: KeyboardShortcut) => void;
  unregisterShortcut: (id: string) => void;
  showHelp: () => void;
  hideHelp: () => void;
  isHelpOpen: boolean;
}

const KeyboardNavContext = createContext<KeyboardNavContextType>({
  shortcuts: [],
  registerShortcut: () => {},
  unregisterShortcut: () => {},
  showHelp: () => {},
  hideHelp: () => {},
  isHelpOpen: false
});

export const useKeyboardNav = () => useContext(KeyboardNavContext);

// Hook for registering a keyboard shortcut
export const useKeyboardShortcut = (
  keys: string[],
  callback: () => void,
  options?: {
    id?: string;
    description?: string;
    category?: string;
    enabled?: boolean;
  }
) => {
  const { registerShortcut, unregisterShortcut } = useKeyboardNav();

  useEffect(() => {
    const id = options?.id || keys.join('+');
    registerShortcut({
      id,
      keys,
      description: options?.description || 'Custom shortcut',
      category: options?.category || 'General',
      action: callback,
      enabled: options?.enabled !== false
    });

    return () => unregisterShortcut(id);
  }, [keys.join('+'), callback, options?.enabled]);
};

// Keyboard navigation provider
interface KeyboardNavProviderProps {
  children: React.ReactNode;
  defaultShortcuts?: KeyboardShortcut[];
}

export const KeyboardNavProvider: React.FC<KeyboardNavProviderProps> = ({
  children,
  defaultShortcuts = []
}) => {
  const [shortcuts, setShortcuts] = useState<KeyboardShortcut[]>(defaultShortcuts);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    setShortcuts(prev => {
      const exists = prev.find(s => s.id === shortcut.id);
      if (exists) {
        return prev.map(s => s.id === shortcut.id ? shortcut : s);
      }
      return [...prev, shortcut];
    });
  }, []);

  const unregisterShortcut = useCallback((id: string) => {
    setShortcuts(prev => prev.filter(s => s.id !== id));
  }, []);

  // Global keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement).isContentEditable
      ) {
        return;
      }
      
      // Build key combo string
      const combo: string[] = [];
      if (e.ctrlKey || e.metaKey) combo.push('Ctrl');
      if (e.altKey) combo.push('Alt');
      if (e.shiftKey) combo.push('Shift');
      if (e.key && e.key !== 'Control' && e.key !== 'Alt' && e.key !== 'Shift' && e.key !== 'Meta') {
        combo.push(e.key.toUpperCase());
      }
      
      const comboStr = combo.join('+');

      // Check for matching shortcut
      const matchingShortcut = shortcuts.find(s => {
        if (s.enabled === false) return false;
        const shortcutCombo = s.keys.map(k => k.toUpperCase()).join('+');
        return shortcutCombo === comboStr;
      });

      if (matchingShortcut) {
        e.preventDefault();
        matchingShortcut.action();
      }

      // Show help with ?
      if (e.key === '?' && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setIsHelpOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);

  return (
    <KeyboardNavContext.Provider
      value={{
        shortcuts,
        registerShortcut,
        unregisterShortcut,
        showHelp: () => setIsHelpOpen(true),
        hideHelp: () => setIsHelpOpen(false),
        isHelpOpen
      }}
    >
      {children}
      <KeyboardHelpDialog
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        shortcuts={shortcuts}
      />
    </KeyboardNavContext.Provider>
  );
};

// Keyboard help dialog
interface KeyboardHelpDialogProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts: KeyboardShortcut[];
}

const KeyboardHelpDialog: React.FC<KeyboardHelpDialogProps> = ({
  isOpen,
  onClose,
  shortcuts
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Group shortcuts by category
  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) acc[shortcut.category] = [];
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, KeyboardShortcut[]>);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyboard-help-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <Keyboard className="w-5 h-5 text-gray-700" />
            </div>
            <div>
              <h2 id="keyboard-help-title" className="font-semibold text-gray-800">
                Keyboard Shortcuts
              </h2>
              <p className="text-sm text-gray-500">Press ? anytime to show this help</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Shortcuts list */}
        <div className="p-4 overflow-y-auto max-h-96">
          {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
            <div key={category} className="mb-6 last:mb-0">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                {category}
              </h3>
              <div className="space-y-2">
                {categoryShortcuts.map((shortcut) => (
                  <div
                    key={shortcut.id}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg"
                  >
                    <span className="text-sm text-gray-700">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, idx) => (
                        <React.Fragment key={idx}>
                          <kbd className="px-2 py-1 text-xs font-mono bg-gray-100 border border-gray-300 rounded text-gray-700">
                            {key === 'Ctrl' ? '⌘' : key}
                          </kbd>
                          {idx < shortcut.keys.length - 1 && (
                            <span className="text-gray-400 text-xs">+</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Enter</kbd>
              Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Esc</kbd>
              Close
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Roving tabindex hook for list navigation
export const useRovingTabindex = <T extends HTMLElement>(
  items: React.RefObject<T>[],
  options?: {
    orientation?: 'horizontal' | 'vertical' | 'both';
    loop?: boolean;
    onSelect?: (index: number) => void;
  }
) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const orientation = options?.orientation || 'vertical';
  const loop = options?.loop !== false;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const isVertical = orientation === 'vertical' || orientation === 'both';
      const isHorizontal = orientation === 'horizontal' || orientation === 'both';
      
      let newIndex = focusedIndex;

      if (isVertical && e.key === 'ArrowDown') {
        e.preventDefault();
        newIndex = loop
          ? (focusedIndex + 1) % items.length
          : Math.min(focusedIndex + 1, items.length - 1);
      } else if (isVertical && e.key === 'ArrowUp') {
        e.preventDefault();
        newIndex = loop
          ? (focusedIndex - 1 + items.length) % items.length
          : Math.max(focusedIndex - 1, 0);
      } else if (isHorizontal && e.key === 'ArrowRight') {
        e.preventDefault();
        newIndex = loop
          ? (focusedIndex + 1) % items.length
          : Math.min(focusedIndex + 1, items.length - 1);
      } else if (isHorizontal && e.key === 'ArrowLeft') {
        e.preventDefault();
        newIndex = loop
          ? (focusedIndex - 1 + items.length) % items.length
          : Math.max(focusedIndex - 1, 0);
      } else if (e.key === 'Home') {
        e.preventDefault();
        newIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        newIndex = items.length - 1;
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        options?.onSelect?.(focusedIndex);
        return;
      }

      if (newIndex !== focusedIndex) {
        setFocusedIndex(newIndex);
        items[newIndex]?.current?.focus();
      }
    },
    [focusedIndex, items, orientation, loop, options]
  );

  const getItemProps = (index: number) => ({
    tabIndex: index === focusedIndex ? 0 : -1,
    onKeyDown: handleKeyDown,
    onFocus: () => setFocusedIndex(index)
  });

  return {
    focusedIndex,
    setFocusedIndex,
    getItemProps
  };
};

// Keyboard shortcut indicator component
interface ShortcutHintProps {
  keys: string[];
  className?: string;
}

export const ShortcutHint: React.FC<ShortcutHintProps> = ({
  keys,
  className = ''
}) => (
  <div className={`flex items-center gap-0.5 ${className}`}>
    {keys.map((key, idx) => (
      <React.Fragment key={idx}>
        <kbd className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 border border-gray-300 rounded text-gray-600">
          {key === 'Ctrl' ? (navigator.platform.includes('Mac') ? '⌘' : 'Ctrl') : key}
        </kbd>
        {idx < keys.length - 1 && (
          <span className="text-gray-400 text-xs">+</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

export default {
  KeyboardNavProvider,
  useKeyboardNav,
  useKeyboardShortcut,
  useRovingTabindex,
  ShortcutHint
};
